<template>
  <div id="map" class="map-container">
    <TrailDataCard/>
    <MapControls/>
    <MapButtons :button-clicked="buttonTapped" :buttons="buttons"/>
    <BaseMapStylesDialog />
    <LoadingDialog :show-loading-dialog="loading" />
    <MapDataPOIDetailDialog/>
    <div class="bottom-controls-container">
      <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="mapInstance?.getCenter()?.lat || 0" />
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import BaseMapStylesDialog from '../../apps/./base_map_dialog/BaseMapStylesDialog.vue';
import MapButtons from '../components/MapButtons.vue';
import MapControls from '../components/MapControls.vue';
import TrailDataCard from '../../apps/trail_data_card/TrailDataCard.vue';
import graph_client from '../../utils/graphql/graph_client';
import { HiikerMapStyle, ThreeDimensionalMapManager } from '../../utils/models/MapStyles';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { GetPtv2PlannedTrailBySlug } from "../../utils/graphql/planned-trail-graph";
import { GetRecordedTrailById, GetRecordedTrailByRecordingId } from "../../utils/graphql/recorded-trail-graph";
import LoadingDialog from "../../utils/shared_components/LoadingDialog.vue";
import MapOptionsDialog from '../map_options_dialog/MapOptionsDialog.vue';
import { Modal } from 'bootstrap';
import {LayerRegistry} from "../../utils/layer-registry";
import emitter from "../../utils/bus";
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";
import ScaleBar from "../components/ScaleBar.vue";

export default {
  name: 'PlanMap',
  components: {
    MapDataPOIDetailDialog,
    LoadingDialog,
    BaseMapStylesDialog,
    MapButtons,
    MapControls,
    TrailDataCard,
    MapOptionsDialog,
    ScaleBar,
  },
  data() {
    return {
      loading: true,
      buttons: [
        {
          id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true,
        },
        {
          id: 1, name: '3D maps', icon: 'landscape', enabled: false, dropIcon: false,
        },
      ],
      geojson: null,
      mapInstance: null,
      trailType: 'planned', // 'planned' or 'recording'
      recording: null,
    };
  },
  async mounted() {
    console.log('🏁 PlanMap.vue - Component mounted');
    console.log('🔍 Initial state:', {
      trailType: this.trailType,
      loading: this.loading,
      hasGeojson: !!this.geojson
    });

    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';
    const isPro = await RevenueCatWrapper.isProOrProPlus();
    const mapStyle = HiikerMapStyle.getCurrentStyle(isPro);
    console.log('🗺️ Creating Mapbox map with style:', mapStyle.styleUrl);
    window.map = new mapboxgl.Map({
      container: 'map',
      style: mapStyle.styleUrl,
      center: window.lonlat || [0, 0],
      zoom: 12,
    });
    this.mapInstance = window.map; // Store instance locally for scale bar

    window.layerRegistry = new LayerRegistry(window.map);

    // Handle resize events to ensure the map fills the screen
    window.addEventListener('resize', this.resizeMap);
    this.resizeMap();

    // Add a map on load
    window.map.on('load', async () => {
      console.log('🗺️ Mapbox map loaded event fired');
      emitter.emit('map-loaded');
      await this.fetchData();
      this.addToMap();
    });
    window.map.on('style.load', () => {
      console.log('🎨 Mapbox style loaded event fired');
      emitter.emit('map-style-loaded');
      this.addToMap();
    });
    // Re-add the trail line whenever other components trigger a style reload
    emitter.on('map-style-loaded', this.addToMap);
  },
  methods: {
    detectTrailType() {
      // Check URL parameters for trail_type
      const urlParams = new URLSearchParams(window.location.search);
      const trailType = urlParams.get('trail_type');
      const trailId = urlParams.get('trail_id');

      console.log('🔍 detectTrailType - URL params:', {
        trail_type: trailType,
        trail_id: trailId,
        fullUrl: window.location.href
      });

      if (trailType === 'Recording') {
        this.trailType = 'recording';
        console.log('✅ Trail type detected as: recording');
        return 'recording';
      }

      // Default to planned trail
      this.trailType = 'planned';
      console.log('✅ Trail type detected as: planned');
      return 'planned';
    },

    setupMapCenter() {
      // Set appropriate map center based on trail type
      const trailType = this.detectTrailType();

      if (trailType === 'recording' && this.recording?.startAddress) {
        // For recordings, we might want to use the start address coordinates
        // This is optional - the map will auto-fit to the line bounds anyway
        const urlParams = new URLSearchParams(window.location.search);
        const lat = urlParams.get('lat');
        const lng = urlParams.get('lng');

        if (lat && lng) {
          // Use coordinates from URL parameters if provided
          window.map.setCenter([parseFloat(lng), parseFloat(lat)]);
        }
        // Otherwise, the map will auto-fit to the line bounds in addToMap()
      }
      // For planned trails, the existing window.lonlat logic in mounted() handles this
    },

    resizeMap() {
      // Ensure map resizes when window size changes
      if (window.map) {
        window.map.resize();
      }
    },
    async buttonTapped(button) {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      switch (button) {
        case 0:
          // Map Layers
          if (window.user) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1:
          // 3D maps
          if (isProOrProPlus) {
            ThreeDimensionalMapManager.toggle3DMode();
          } else {
            window.location.href = '/purchases';
          }
          break;
        default:
          break;
      }
    },
    async fetchData() {
      console.log('🚀 fetchData - Starting data fetch');
      const trailType = this.detectTrailType();

      if (trailType === 'recording') {
        console.log('📥 Processing recording data');
        // Handle recording data
        const urlParams = new URLSearchParams(window.location.search);
        const trailId = urlParams.get('trail_id');

        console.log('🔍 Recording trail_id from URL:', trailId);

        if (!trailId) {
          console.error('❌ Recording trail_id not found in URL parameters');
          this.loading = false;
          return;
        }

        try {
          console.log('📡 Making GraphQL request for recording ID:', trailId);
          const response = await graph_client.request(GetRecordedTrailByRecordingId, { id: trailId });
          console.log('✅ GraphQL response received:', response);

          const { recording } = response;
          this.recording = recording;
          console.log('📊 Recording data loaded:', {
            id: recording?.id,
            title: recording?.title,
            hasLineString: !!recording?.lineString,
            lineStringLength: recording?.lineString?.length
          });

          if (recording.lineString) {
            console.log('📐 Parsing GeoJSON from lineString');
            const geojson = JSON.parse(recording.lineString);
            console.log('🗺️ GeoJSON parsed:', {
              type: geojson?.type,
              featuresCount: geojson?.features?.length,
              coordinatesCount: geojson?.features?.[0]?.geometry?.coordinates?.length
            });
            this.geojson = geojson;
            console.log('✅ GeoJSON stored in component data');
            window.trailCardDataRecording(recording);
            this.setupMapCenter();
          } else {
            console.error('❌ Recording lineString not found');
            this.loading = false;
          }
        } catch (error) {
          console.error('❌ Error fetching recording data:', error);
          this.loading = false;
        }
      } else {
        console.log('📥 Processing planned trail data');
        // Handle planned trail data (original logic)
        console.log('🔍 Planned trail plan_id:', window.plan_id);
        const response = await graph_client.request(GetPtv2PlannedTrailBySlug, { slug: window.plan_id });
        const { ptv2PlannedTrailBySlug } = response;
        const geojson = JSON.parse(ptv2PlannedTrailBySlug.lineStringFeatureCollection);
        this.geojson = geojson;
        window.trailCardDataPtv2PlannedTrail(ptv2PlannedTrailBySlug);
      }
      console.log('🏁 fetchData - Completed');
    },
    addToMap() {
      console.log('🎯 addToMap - Starting map rendering');
      console.log('🔍 addToMap - this.geojson:', this.geojson);

      if (!this.geojson) {
        console.log('❌ addToMap - No geojson data available');
        return;
      }

      console.log('🗺️ addToMap - GeoJSON structure:', {
        type: this.geojson.type,
        featuresCount: this.geojson.features?.length,
        firstFeature: this.geojson.features?.[0]
      });

      if (window.map.getSource('line')) {
        console.log('🔄 Removing existing line source and layer');
        window.map.removeLayer('line');
        window.map.removeSource('line');
      }

      console.log('➕ Adding line source to map');
      window.map.addSource('line', {
        type: 'geojson',
        data: this.geojson,
      });

      console.log('➕ Adding line layer to map');
      window.map.addLayer({
        id: 'line',
        type: 'line',
        source: 'line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#a72024',
          'line-width': 8,
        },
      });

      console.log('📍 Setting map bounds');
      // set the bounds
      const bounds = new mapboxgl.LngLatBounds();
      const { geometry } = this.geojson.features[0];
      console.log('📏 Geometry coordinates count:', geometry.coordinates.length);
      geometry.coordinates.forEach((coord) => {
        bounds.extend([coord[0], coord[1]]);
      });
      window.map.fitBounds(bounds, { padding: 150 });

      this.loading = false;
      console.log('✅ addToMap - Map rendering completed');
    },
  },
  beforeDestroy() {
    // Clean up event listener when component is destroyed
    window.removeEventListener('resize', this.resizeMap);
    emitter.off('map-style-loaded', this.addToMap);

    // Clean up map resources
    if (window.map) {
      // Remove sources and layers
      if (window.map.getSource('line')) {
        window.map.removeLayer('line');
        window.map.removeSource('line');
      }

      // Remove the map instance
      window.map.remove();
      window.map = null;
    }
  },
};
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 90svh;
  position: relative;
}

@media (max-width: 520px){
  .map-buttons-container{
    bottom: 190px !important;
  }
}
</style>
